import { rgba, darken } from 'polished';
import deepmerge from 'deepmerge';
import { createTheme } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import colors, { WHITE, BLACK, LIGHTGREY, MISCHKA } from '@/constants/colors';

const baseTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export const theme = createTheme({
  ...baseTheme,
  palette: {
    primary: {
      main: colors.primary,
      contrastText: colors.primaryText,
    },
    secondary: {
      main: colors.secondary,
    },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
    },
    background: {
      default: colors.bg,
    },
    success: {
      main: colors.primary,
      contrastText: colors.primaryText,
    },
    error: {
      main: colors.error,
      contrastText: colors.primaryText,
    },
    warning: {
      main: colors.warning,
      contrastText: colors.text,
    },
    mode: 'light',
  },
  typography: {
    useNextVariants: true,
    // fontFamily: ['Gilroy', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    fontFamily: "'Gilroy', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'",
    h1: {
      fontSize: '6rem',
      lineHeight: '6rem',
      fontWeight: 400,

      [baseTheme.breakpoints.down('xs')]: {
        fontSize: '4.6rem',
        lineHeight: '5rem',
      },
    },
    h2: {
      fontSize: '4.2rem',
      lineHeight: '4.6rem',
      fontWeight: 400,

      [baseTheme.breakpoints.down('xs')]: {
        fontSize: '3.2rem',
        lineHeight: '3.8rem',
      },

      '& span[role="img"]': {
        fontSize: '3.2rem',
      },
    },
    h3: {
      fontSize: '3.6rem',
      lineHeight: '3.6rem',
      fontWeight: 700,

      [baseTheme.breakpoints.down('xs')]: {
        fontSize: '3rem',
        lineHeight: '3rem',
      },
    },
    h4: {
      fontSize: '3rem',
      lineHeight: '3rem',
      fontWeight: 700,
    },
    h5: {
      fontSize: '2rem',
      lineHeight: '2rem',
      fontWeight: 700,
    },
    h6: {
      fontSize: '1.8rem',
      lineHeight: '1.8rem',
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: '1.8rem',
      lineHeight: '1.8rem',
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: '1.8rem',
      lineHeight: '2.4rem',
      fontWeight: 400,
    },
    body1: {
      fontSize: '1.8rem',
      lineHeight: '3rem',
      fontWeight: 400,
    },
    body2: {
      fontSize: '1.8rem',
      lineHeight: '3rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        color: 'inherit',
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiCircularProgress-root': {
            position: 'absolute',
            right: '15px',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.bg,
            boxShadow: `0 10px 30px ${rgba(BLACK, 0.1)}`,
          },
        },
        inputRoot: {
          '&[class*="MuiOutlinedInput-root"][class*="MuiInputBase-sizeSmall"]': {
            padding: '10px 25px 10px 12px',
          },
        },
        hasPopupIcon: {
          '&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
            paddingRight: '6.5rem !important',
          },
        },
        paper: {
          fontSize: '1.4rem',
          lineHeight: '1.4rem',
          fontWeight: '500',
          marginTop: 0,
          marginBottom: 0,
          borderRadius: '0 0 3px 3px',
          padding: '0',
          boxShadow: `0 20px 30px ${rgba(BLACK, 0.1)}`,
        },
        listbox: {
          padding: '10px 0',

          [baseTheme.breakpoints.down('xs')]: {
            maxHeight: '20vh',
          },
        },
        option: {
          minHeight: '36px !important',
          paddingTop: '6px',
          paddingBottom: '6px',
          paddingLeft: '15px',
          paddingRight: '15px',

          '&.Mui-focused': {
            backgroundColor: colors.listItem.bg,
          },
          '&[aria-selected="true"]': {
            color: colors.primary,
            backgroundColor: `${colors.listItem.bg} !important`,
          },
        },
        popupIndicator: {
          top: '0',
        },
        clearIndicator: {
          top: '0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'inherit',
          borderRadius: '2rem',
          padding: '1.3rem 2rem',
          fontSize: '1.4rem',
          lineHeight: '1.4rem',

          // '&:hover': {
          //   backgroundColor: colors.buttons.bgHover,
          // },

          '&.sizeExtraLarge': {
            fontSize: '1.6rem',
            lineHeight: '1.6rem',
            padding: '1.5rem 3.2rem',
            borderRadius: '2.3rem',

            '&.MuiButton-outlined': {
              padding: '1.3rem 3rem',
            },
          },
        },
        sizeLarge: {
          fontSize: '1.6rem',
          lineHeight: '1.6rem',
          padding: '1.7rem 3.5rem',
          borderRadius: '2.5rem',

          '&.MuiButton-outlined.MuiButton-sizeLarge': {
            padding: '1.5rem 3.3rem',
          },
        },
        sizeSmall: {
          fontSize: '1.4rem',
          lineHeight: '1.4rem',
          padding: '1.1rem 2rem',

          '&.MuiButton-outlined.MuiButton-sizeSmall': {
            padding: '0.9rem 1.8rem',
          },
        },
        iconSizeLarge: {
          '&> *:first-child': {
            fontSize: '1.8rem',
            marginTop: '-1px',
            marginBottom: '-1px',
          },
        },
        iconSizeMedium: {
          '&> *:first-child': {
            fontSize: '1.6rem',
            marginTop: '-1px',
            marginBottom: '-1px',
          },
        },
        iconSizeSmall: {
          '&> *:first-child': {
            fontSize: '1.6rem',
            marginTop: '-2px',
            marginBottom: '-2px',
          },
        },
        contained: {
          boxShadow: 'none',

          '&:active, &:hover': {
            boxShadow: 'none',
          },

          '&.danger': {
            backgroundColor: colors.buttons.danger.contained.bg,
            color: colors.buttons.danger.contained.text,

            '&:hover': {
              backgroundColor: colors.buttons.danger.contained.bgHover,
            },

            '&.Mui-disabled': {
              color: rgba(BLACK, 0.26),
              backgroundColor: rgba(BLACK, 0.12),
            },
          },

          '&.default': {
            backgroundColor: colors.buttons.default.bg,
            color: colors.buttons.default.color,

            '&:hover': {
              backgroundColor: colors.buttons.default.bgHover,
            },

            '&.Mui-disabled': {
              color: rgba(BLACK, 0.26),
              backgroundColor: rgba(BLACK, 0.12),
            },
          },
        },
        outlined: {
          padding: '1.1rem 1.8rem',

          '&.Mui-disabled': {
            borderWidth: '0.2rem',
          },

          '&.danger': {
            borderColor: colors.buttons.danger.outlined.border,
            color: colors.buttons.danger.outlined.text,

            '&:hover': {
              backgroundColor: colors.buttons.danger.outlined.bgHover,
            },

            '&.Mui-disabled': {
              color: rgba(BLACK, 0.26),
              backgroundColor: rgba(BLACK, 0.12),
            },
          },

          '&.default': {
            backgroundColor: colors.buttons.default.outlined.bg,
            color: colors.buttons.default.outlined.color,
            border: `0.2rem solid ${colors.buttons.default.outlined.border}`,

            '&:hover': {
              backgroundColor: colors.buttons.default.bgHover,
            },

            '&.Mui-disabled': {
              borderColor: rgba(BLACK, 0.12),
              color: rgba(BLACK, 0.26),
            },
          },

          '&.MuiButton-colorPrimary': {
            border: `0.2rem solid ${colors.primary}`,

            '&:hover': {
              borderWidth: '0.2rem',
            },
          },

          '&.MuiButton-colorSecondary': {
            border: `0.2rem solid ${colors.secondary}`,

            '&:hover': {
              borderWidth: '0.2rem',
            },

            '&.Mui-disabled': {
              borderWidth: '0.2rem',
            },
          },
        },
        text: {
          '&.default': {
            backgroundColor: colors.buttons.default.bg,
            color: colors.buttons.default.color,

            '&:hover': {
              backgroundColor: colors.buttons.default.bgHover,
            },

            '&.Mui-disabled': {
              backgroundColor: rgba(BLACK, 0.12),
              color: rgba(BLACK, 0.26),
            },
          },
        },
      },
      defaultProps: {
        size: 'large',
        variant: 'contained',
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          lineHeight: '1.6rem',
          color: colors.text,

          '& .MuiLink-root': {
            color: colors.breadcrumbs.link,
            transition: 'color 0.3s ease',

            '&:hover': {
              color: colors.primary,
            },
          },
        },

        separator: {
          marginRight: 10,
          marginLeft: 10,
          color: colors.breadcrumbs.link,
          fontSize: '0.8rem',
          padding: 3,
        },

        li: {
          '&::first-letter': {
            textTransform: 'capitalize',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '24px',
          fontSize: '1.2rem',
          fontWeight: 600,
          lineHeight: '2.4rem',
          color: colors.chip.text,
          backgroundColor: colors.chip.bg,
          borderRadius: '2px',
        },
        clickable: {
          '&:active': {
            boxShadow: 'none',
          },
          '&:hover, &:focus': {
            backgroundColor: colors.chip.hoverBg,
          },
        },
        label: {
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        deleteIcon: {
          height: '18px',
          width: '18px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,

          '& .icon-correct-symbol': {
            '&.default:before': {
              content: '""',
              borderColor: colors.checkbox.border,
            },

            '&:before': {
              height: '22px',
              width: '22px',
              borderRadius: '3px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: `1px solid ${colors.primary}`,
            },
          },
        },
        colorPrimary: {
          '&.Mui-checked': {
            color: colors.primaryText,

            '& .icon-correct-symbol': {
              '&:before': {
                backgroundColor: colors.primary,
              },
            },

            '& + .MuiFormControlLabel-label': {
              color: colors.primary,
            },
          },
          '&.Mui-checked:hover': {
            backgroundColor: 'transparent !important',
          },
        },
      },
      defaultProps: {
        icon: <i className="icon-correct-symbol default" />,
        checkedIcon: <i className="icon-correct-symbol" />,
        disableRipple: true,
        color: 'primary',
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.divider,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          display: 'flex',
          width: '240px',
        },
        paper: {
          border: 'none',
          background: colors.secondary,
          padding: '0px',
          width: '240px',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '&.sizeSmall': {
            '& .MuiInputLabel-formControl': {
              transform: 'translate(13px, 12px) scale(1)',
            },
            '.MuiInputLabel-formControl.MuiInputLabel-shrink': {
              transform: 'translate(8px, -5px) scale(0.75)',
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
        label: {
          fontSize: '1.4rem',
          lineHeight: '1.4rem',
          fontWeight: 500,
          marginLeft: '8px',
        },
      },
      defaultProps: {
        className: 'noselect',
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
          lineHeight: '1.6rem',
          fontWeight: 500,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          lineHeight: '1.4rem',
          fontWeight: 400,
          marginTop: '.8rem',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        'spacing-xs-10': { margin: 0, width: '100%' },
        'spacing-xs-8': { margin: 0, width: '100%' },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'large',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
          lineHeight: '1.6rem',
          fontWeight: 500,
          backgroundColor: colors.inputs.background,

          '&.Mui-disabled': {
            '& .MuiSelect-icon': {
              fill: rgba(BLACK, 0.26),
            },
          },

          '&.sizeSmall': {
            fontSize: '1.4rem',
            lineHeight: '1.4rem',

            '& .MuiSelect-select': {
              minHeight: '4rem',
            },

            '& .MuiSelect-icon': {
              fontSize: '1.6rem',
              top: 'calc(50% - 8px)',
            },

            '& .MuiSelect-outlined': {
              padding: '1.3rem',

              '&.MuiSelect-outlined': {
                paddingRight: '3rem',
              },

              '& ~ .MuiSelect-icon': {
                right: '1rem',
              },
            },
          },
        },
        input: {
          height: 'auto',
          padding: '2rem 3rem',
          borderRadius: '.3rem',
          boxSizing: 'border-box',

          '&::-webkit-input-placeholder': {
            color: colors.text,
            opacity: 1,
          },
          '-webkit-appearance': 'none',
        },
        inputAdornedStart: {
          paddingLeft: 0,
        },
        inputSizeSmall: {
          '&.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
        },
        sizeSmall: {
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '1.5rem',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        formControl: {
          transform: 'translate(13px, 22px) scale(1)',
          zIndex: 1,
          pointerEvents: 'none',
          backgroundColor: colors.inputs.background,
          padding: '0 5px',

          '&.MuiInputLabel-shrink': {
            transform: 'translate(8px, -5px) scale(0.75)',
          },
        },
        outlined: {
          transform: 'translate(13px, 22px) scale(1)',

          '&.MuiInputLabel-shrink': {
            transform: 'translate(18px, -5px) scale(0.75)',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: colors.inputs.defaultIcon,

          '&.success': {
            color: colors.primary,
          },
        },
        sizeSmall: {
          fontSize: '1.8rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&[disabled]': {
            cursor: 'default',
            color: MISCHKA,
          },
        },
      },
      defaultProps: {
        underline: 'none',
        variant: 'button',
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
          },
        },
        padding: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover, &:hover': {
            backgroundColor: colors.listItem.bg,

            '&.secondary': {
              backgroundColor: colors.bg,
            },
          },
        },
        gutters: {
          paddingLeft: '20px',
          paddingRight: '20px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover, &:hover': {
            backgroundColor: colors.listItem.bg,

            '&.secondary': {
              backgroundColor: colors.bg,
            },
          },
        },
        gutters: {
          paddingLeft: '20px',
          paddingRight: '20px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 14,
          borderRadius: 7,
        },
        bar: {
          borderRadius: 7,
        },
        colorPrimary: {
          backgroundColor: colors.linearProgress.bg,
        },
        barColorPrimary: { backgroundColor: colors.linearProgress.barBg },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          '&.MuiPaper-elevation8': {
            boxShadow: `0px 10px 20px ${rgba(BLACK, 0.1)}`,
          },
          '&.MuiPaper-rounded': {
            borderRadius: '3px',
          },
        },
      },
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          lineHeight: '1.4rem',
          fontWeight: 500,
          paddingTop: '16px',
          paddingBottom: '16px',

          '&.Mui-selected, &.Mui-selected:hover, &:hover': {
            backgroundColor: colors.listItem.bg,

            '&.secondary': {
              backgroundColor: colors.bg,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '0.3rem',
        },
        input: {
          padding: '2rem 3rem',
        },
        multiline: {
          padding: '2rem 3rem',
        },
        notchedOutline: {
          borderColor: colors.inputs.border,
        },
        inputSizeSmall: {
          padding: '0 1.5rem',
          paddingTop: '1.15rem',
          paddingBottom: '1.15rem',
          fontSize: '1.4rem',
          lineHeight: '1.4rem',

          '& > .MuiInputAdornment-root': {
            fontSize: '1rem !important',
          },
        },
        adornedStart: {
          paddingLeft: '2.5rem',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          margin: '70px 0',

          [baseTheme.breakpoints.down('xs')]: {
            margin: '35px 0',
          },
        },
        ul: {
          justifyContent: 'center',
        },
      },
      defaultProps: {
        color: 'secondary',
        shape: 'circular',
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontSize: '1.2rem',
          lineHeight: '1.2rem',
          fontWeight: 600,
          height: '30px',
          minWidth: '30px',
          margin: '0 5px',
        },
        rounded: {
          borderRadius: '2px',
        },
        icon: {
          fontSize: '1.6rem',
        },
        page: {
          '&.Mui-disabled': {
            opacity: 1,
            color: LIGHTGREY,
          },
        },
        text: {
          '.MuiPaginationItem-colorSecondary': {
            '&:hover': {
              backgroundColor: colors.secondary,
              color: colors.primaryText,
            },
            '&.Mui-selected': {
              boxShadow: `0px 6px 10px ${rgba(BLACK, 0.1)}`,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: '6px',
        },
        elevation1: {
          boxShadow: 'none',
          padding: '2.6rem 3rem',
        },
        elevation4: {
          boxShadow: `0px 1px 0 ${colors.paper.elevation4}`,
        },
        elevation5: {
          boxShadow: `0px 1px 20px ${colors.paper.elevation5}`,
        },
        elevation8: {
          boxShadow: `0px 2px 4px ${colors.paper.boxShadow}`,
        },
        elevation9: {
          boxShadow: `0px 20px 40px ${colors.paper.elevation9}`,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '1.3rem',
        },
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
          lineHeight: '1.6rem',
          fontWeight: 500,
          backgroundColor: colors.inputs.background,
          padding: '0 1rem',

          '&.Mui-disabled': {
            '& .MuiSelect-icon': {
              fill: rgba(BLACK, 0.26),
            },
          },

          '&.sizeSmall': {
            fontSize: '1.4rem',
            lineHeight: '1.4rem',

            '& .MuiSelect-select': {
              minHeight: '4rem',
            },

            '& .MuiSelect-icon': {
              fontSize: '1.6rem',
              top: 'calc(50% - 8px)',
            },

            '& .MuiSelect-outlined': {
              padding: '1.3rem',

              '&.MuiSelect-outlined': {
                paddingRight: '3rem',
              },

              '& ~ .MuiSelect-icon': {
                right: '1rem',
              },
            },
          },
        },
        input: {
          height: 'auto',
          padding: '2rem 3rem',
          borderRadius: '.3rem',
          boxSizing: 'border-box',

          '&::-webkit-input-placeholder': {
            color: colors.text,
            opacity: 1,
          },
          '-webkit-appearance': 'none',
        },
        inputAdornedStart: {
          paddingLeft: 0,
        },
        inputSizeSmall: {
          '&.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
        },
        sizeSmall: {
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '1.5rem',
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '0.3rem',
        },
        input: {
          padding: '2rem 3rem',
        },
        multiline: {
          padding: '2rem 3rem',
        },
        notchedOutline: {
          borderColor: colors.inputs.border,
        },
        inputSizeSmall: {
          padding: '0 1.5rem',
          paddingTop: '1.15rem',
          paddingBottom: '1.15rem',
          fontSize: '1.4rem',
          lineHeight: '1.4rem',

          '& > .MuiInputAdornment-root': {
            fontSize: '1rem !important',
          },
        },
        adornedStart: {
          paddingLeft: '2.5rem',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          minHeight: 'auto',
          minWidth: 'auto',
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          color: colors.rating.active,
          fontSize: '1.8rem',

          '& .MuiRating-decimal': {
            '&:not(:last-of-type)': {
              marginRight: '8px',
            },
          },

          '& .MuiRating-label': {
            '&:not(:last-of-type) .MuiRating-icon': {
              marginRight: '8px',
            },
          },

          '&.sizeLarge': {
            '& .MuiRating-iconActive': {
              transform: 'none',
            },

            '& .MuiRating-iconEmpty': {
              color: colors.rating.large.color,
              backgroundColor: colors.rating.large.bg,
            },

            '& .MuiRating-iconFilled': {
              color: colors.rating.large.activeColor,
              backgroundColor: colors.rating.large.activeBg,
            },

            '& .MuiRating-icon': {
              padding: 16,
              borderRadius: 3,
              boxShadow: `0px 1px 3px ${colors.rating.large.shadow}`,
              border: `1px solid ${colors.rating.large.border}`,
            },

            '&:not(:last-of-type)': {
              '& .MuiRating-icon': {
                marginRight: '15px',
              },
            },
          },
        },
        iconEmpty: {
          color: colors.rating.default,
        },
      },
      defaultProps: {
        icon: <i className="icon-star-solid" />,
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: '5.9rem',
          '&:focus': {
            borderRadius: '.3rem',
            backgroundColor: WHITE,
          },
        },
        outlined: {
          borderRadius: '.3rem',
          padding: '2.1rem 3rem',

          '&.MuiSelect-outlined': {
            paddingRight: '5rem',
          },

          '& ~ .MuiSelect-icon': {
            right: '2.5rem',
          },
        },
        icon: {
          fontSize: '2.4rem',
          top: 'calc(50% - 12px)',
          fill: colors.text,
        },
      },
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        IconComponent: KeyboardArrowDown,
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          bg: colors.skeleton.bg,
        },
        text: {
          borderRadius: '6.5px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: '69px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          lineHeight: '1.6rem',
          padding: '5px 10px',
          borderBottomColor: colors.table.cellBorder,

          '&:first-of-type': {
            paddingLeft: '20px',
          },
          '&:last-of-type': {
            paddingRight: '20px',
          },
        },
        head: {
          fontSize: '1.4rem',
          lineHeight: '1.6rem',
          fontWeight: 600,
          color: colors.primary,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: colors.table.pagination,
        },
        toolbar: {
          paddingRight: '24px',
          '& .MuiInputBase-input': {
            minWidth: '50px',
            border: 'none',
            marginTop: 0,
            fontSize: '1.2rem',
          },
        },
        selectIcon: {
          top: 'calc(50% - 1rem)',
          right: '.4rem',
          fill: colors.table.pagination,
          fontSize: '2rem',
        },
        select: {
          minHeight: 'initial', // override Mui select min-height

          '&.MuiSelect-outlined': {
            paddingRight: '3rem',
          },
        },
        displayedRows: {
          fontSize: '1.4rem',
          lineHeight: '2.6rem',
          marginBottom: 0,
        },
        selectLabel: {
          fontSize: '1.4rem',
          lineHeight: '2.6rem',
          marginBottom: 0,
        },
        caption: {
          fontSize: '1.4rem',
          lineHeight: '2.6rem',
          '&:first-of-type': {
            [baseTheme.breakpoints.down('xs')]: {
              display: 'none',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1.6rem',
          lineHeight: '1.6rem',

          [baseTheme.breakpoints.up('xs')]: {
            minWidth: 0,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.tooltip.bg,
          color: colors.tooltip.text,
          fontSize: '1.2rem',
          lineHeight: '1.4rem',
          fontWeight: 400,
          maxWidth: '452px',
          borderRadius: '3px',
          padding: '6px 10px',

          [baseTheme.breakpoints.down('xs')]: {
            maxWidth: '340px',
          },

          '& > .tooltip-large': {
            fontSize: '1.6rem',
            lineHeight: '2rem',
          },

          '& .tooltip-content': {
            display: 'inline-flex',
            fontSize: '1.2rem',
            lineHeight: '1.4rem',

            '& > button': {
              marginLeft: '5px',
              flexShrink: 0,
            },
          },

          '& button': {
            fontSize: '1.2rem',
            lineHeight: '1.4rem',
          },
        },
        popperArrow: {
          '&[data-popper-placement*="left"]': {
            '& .MuiTooltip-arrow::before': {
              borderColor: `transparent transparent transparent ${colors.tooltip.bg} !important`,
            },
          },
          '&[data-popper-placement*="right"]': {
            '& .MuiTooltip-arrow::before': {
              borderColor: `transparent ${colors.tooltip.bg} transparent transparent !important`,
            },
          },
          '&[data-popper-placement*="top"]': {
            '& .MuiTooltip-arrow::before': {
              borderColor: `${colors.tooltip.bg} transparent transparent transparent !important`,
            },
          },
          '&[data-popper-placement*="bottom"]': {
            '& .MuiTooltip-arrow::before': {
              borderColor: `transparent transparent ${colors.tooltip.bg} transparent !important`,
            },
          },
        },
      },
      defaultProps: {
        arrow: true,
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.m-b-30': {
            marginBottom: 30,
          },
        },
        noWrap: {
          maxWidth: '100%',
        },
        button: {
          fontSize: '1.6rem',
          fontWeight: 600,
          textTransform: 'inherit',
        },
        subtitle2: {
          '& > .MuiLink-root': {
            fontSize: '1.8rem',
            lineHeight: '2.4rem',
          },
        },
      },
      defaultProps: {
        // fontFamily: 'Gilroy',
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'p',
          subtitle2: 'p',
          body1: 'p',
          body2: 'p',
        },
      },
    },
  },
});

export const createNewTheme = (themeConsts = {}) => {
  const fontFamily = themeConsts.primaryFont ? themeConsts.primaryFont.split(', ') : '';

  /*
    primary (HEX string)
    secondary (HEX string)
    primaryButtonTextColor (HEX string)
    secondaryButtonTextColor (HEX string)
    buttonHoverBackground (HEX string)
    background (HEX string)
    inputBackground (HEX string)
    primaryTextColor (HEX string)
    secondarytextcolor (HEX string)
    primaryFont (string divided by , if you want provide a list; For example "'Barlow', sans-serif")
    squareCorners (bool - true|false)
    error (HEX string)
    warning, (HEX string)
  */

  return createTheme(
    deepmerge(theme, {
      partnerStyles: true,
      palette: {
        primary: {
          main: themeConsts.primary || theme.palette.primary.main,
          contrastText: themeConsts.primaryButtonTextColor || theme.palette.primary.contrastText,
          dark: themeConsts.buttonHoverBackground || theme.palette.primary.dark,
        },
        secondary: {
          main: themeConsts.secondary || theme.palette.secondary.main,
          contrastText:
            themeConsts.secondaryButtonTextColor || theme.palette.secondary.contrastText,
          dark: darken(0.1, themeConsts.secondary || theme.palette.secondary.main),
        },
        background: {
          default: themeConsts.background || theme.palette.background.default,
        },
        text: {
          primary: themeConsts.primaryTextColor || theme.palette.text.primary,
          secondary: themeConsts.secondarytextcolor || theme.palette.text.secondary,
        },
        success: {
          main: themeConsts.primary || theme.palette.primary.main,
          contrastText: themeConsts.primaryTextColor || theme.palette.text.primary,
        },
        error: {
          main: themeConsts.error || theme.palette.error.main,
          contrastText: themeConsts.primaryTextColor || theme.palette.text.primary,
        },
        warning: {
          main: themeConsts.warning || theme.palette.warning.main,
          contrastText: themeConsts.primaryTextColor || theme.palette.text.primary,
        },
      },
      typography: {
        fontFamily: fontFamily || theme.typography.fontFamily,
        body1: { fontFamily: fontFamily || theme.typography.fontFamily },
        body2: { fontFamily: fontFamily || theme.typography.fontFamily },
        button: { fontFamily: fontFamily || theme.typography.fontFamily },
        caption: { fontFamily: fontFamily || theme.typography.fontFamily },
        subtitle1: { fontFamily: fontFamily || theme.typography.fontFamily },
        subtitle2: { fontFamily: fontFamily || theme.typography.fontFamily },
        overline: { fontFamily: fontFamily || theme.typography.fontFamily },
        h1: { fontFamily: fontFamily || theme.typography.fontFamily },
        h2: { fontFamily: fontFamily || theme.typography.fontFamily },
        h3: { fontFamily: fontFamily || theme.typography.fontFamily },
        h4: { fontFamily: fontFamily || theme.typography.fontFamily },
        h5: { fontFamily: fontFamily || theme.typography.fontFamily },
        h6: { fontFamily: fontFamily || theme.typography.fontFamily },
      },
      components: {
        MuiBreadcrumbs: {
          styleOverrides: {
            root: {
              '& .MuiLink-root': {
                '&:hover': {
                  color: themeConsts.primary || theme.palette.primary.main,
                },
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor:
                themeConsts.inputBackground ||
                theme.components.MuiInputBase.styleOverrides.root.backgroundColor,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: themeConsts.squareCorners
                ? '0'
                : theme.components.MuiButton.styleOverrides.root.borderRadius,

              '&.sizeExtraLarge': {
                borderRadius: themeConsts.squareCorners
                  ? '0'
                  : theme.components.MuiButton.styleOverrides.root['&.sizeExtraLarge'].borderRadius,
              },
            },
            sizeLarge: {
              borderRadius: themeConsts.squareCorners
                ? '0'
                : theme.components.MuiButton.styleOverrides.sizeLarge.borderRadius,
            },
            outlined: {
              '&.MuiButton-colorPrimary': {
                border: `0.2rem solid ${themeConsts.primary || theme.palette.primary.contrastText}`,
                color: themeConsts.primary || theme.palette.primary.contrastText,
              },
              '&.MuiButton-colorSecondary': {
                border: `0.2rem solid ${
                  themeConsts.secondaryButtonTextColor || theme.palette.secondary.contrastText
                }`,
                color: themeConsts.secondaryButtonTextColor || theme.palette.secondary.contrastText,
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '& .icon-correct-symbol': {
                '&.default:before': {
                  borderColor: themeConsts.primary || theme.palette.primary.main,
                },

                '&:before': {
                  border: `1px solid ${themeConsts.primary || theme.palette.primary.main}`,
                  borderRadius: themeConsts.squareCorners
                    ? '0'
                    : theme.components.MuiCheckbox.styleOverrides.root['& .icon-correct-symbol'][
                        '&:before'
                      ].borderRadius,
                },
              },
            },
            colorPrimary: {
              '&.Mui-checked': {
                color: themeConsts.primaryButtonTextColor || theme.palette.primary.contrastText,

                '& .icon-correct-symbol': {
                  '&:before': {
                    backgroundColor: themeConsts.primary || theme.palette.primary.main,
                  },
                },

                '& + .MuiFormControlLabel-label': {
                  color: themeConsts.primary || theme.palette.primary.main,
                },
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              backgroundColor:
                themeConsts.primary || theme.components.MuiChip.styleOverrides.root.backgroundColor,
              borderRadius: themeConsts.squareCorners
                ? '0'
                : theme.components.MuiChip.styleOverrides.root.borderRadius,
            },
            clickable: {
              '&:hover, &:focus': {
                backgroundColor:
                  themeConsts.primary ||
                  theme.components.MuiChip.styleOverrides.clickable['&:hover, &:focus']
                    .backgroundColor,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              '&.success': {
                color: themeConsts.primary,
              },
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              background: themeConsts.secondary || theme.palette.secondary.main,
            },
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: {
              '&.Mui-selected, &.Mui-selected:hover, &:hover': {
                '&.secondary': {
                  backgroundColor: themeConsts.background || theme.palette.background.default,
                },
              },
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              '&.Mui-selected, &.Mui-selected:hover, &:hover': {
                '&.secondary': {
                  backgroundColor: themeConsts.background || theme.palette.background.default,
                },
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              '&.Mui-selected, &.Mui-selected:hover, &:hover': {
                '&.secondary': {
                  backgroundColor: themeConsts.background || theme.palette.background.default,
                },
              },
            },
          },
        },
        MuiPaginationItem: {
          styleOverrides: {
            root: {
              color:
                themeConsts.primary || theme.components.MuiPaginationItem.styleOverrides.root.color,
              backgroundColor: themeConsts.primaryButtonTextColor || 'transparent',
            },
            text: {
              '.MuiPaginationItem-colorSecondary': {
                '&:hover': {
                  backgroundColor:
                    themeConsts.primary ||
                    theme.components.MuiPaginationItem.styleOverrides.text[
                      '.MuiPaginationItem-colorSecondary'
                    ]['&:hover'].backgroundColor,
                  color:
                    themeConsts.primaryButtonTextColor ||
                    theme.components.MuiPaginationItem.styleOverrides.text[
                      '.MuiPaginationItem-colorSecondary'
                    ]['&:hover'].color,
                },
                '&.Mui-selected': {
                  backgroundColor:
                    themeConsts.primary ||
                    theme.components.MuiPaginationItem.styleOverrides.text[
                      '.MuiPaginationItem-colorSecondary'
                    ]['&.Mui-selected'].backgroundColor,
                  color:
                    themeConsts.primaryButtonTextColor ||
                    theme.components.MuiPaginationItem.styleOverrides.text[
                      '.MuiPaginationItem-colorSecondary'
                    ]['&.Mui-selected'].color,
                },
              },
            },
          },
        },
        MuiPickersInputBase: {
          styleOverrides: {
            root: {
              backgroundColor:
                themeConsts.inputBackground ||
                theme.components.MuiPickersInputBase.styleOverrides.root.backgroundColor,
            },
          },
        },
        MuiRating: {
          styleOverrides: {
            root: {
              color: themeConsts.primary || theme.components.MuiRating.styleOverrides.root.color,

              '&.sizeLarge': {
                '& .MuiRating-iconEmpty': {
                  color:
                    themeConsts.primary ||
                    theme.components.MuiRating.styleOverrides.root['&.sizeLarge'][
                      '& .MuiRating-iconEmpty'
                    ].color,
                },

                '& .MuiRating-iconFilled': {
                  color:
                    themeConsts.primaryButtonTextColor ||
                    theme.components.MuiRating.styleOverrides.root['&.sizeLarge'][
                      '& .MuiRating-iconFilled'
                    ].color,
                  backgroundColor:
                    themeConsts.primary ||
                    theme.components.MuiRating.styleOverrides.root['&.sizeLarge'][
                      '& .MuiRating-iconFilled'
                    ].backgroundColor,
                },

                '& .MuiRating-icon': {
                  border: `1px solid ${themeConsts.primary || colors.rating.large.border}`,
                },
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              color: themeConsts.primary || theme.components.MuiTableCell.styleOverrides.head.color,
            },
          },
        },
      },
    }),
  );
};

export default theme;
